import type { UserProfile } from '@/features/user';
import type { ApiCompetition, ApiParticipant } from './types';

/**
 * Model widokowy listy zawodnikow klubu.
 *
 * API nie ma endpointu „zawodnicy klubu” — zawodnik istnieje wylacznie jako
 * zgloszenie do konkretnej rozgrywki. Liste skladamy wiec ze zgloszen wszystkich
 * rozgrywek klubu: jedna osoba moze wystapic w wielu z nich, a w deblu i mikscie
 * jeden slot niesie dwie osoby.
 *
 * Wszystko tutaj to czyste funkcje — dane przychodza z `GET /api/competitions`
 * oraz `GET /api/competitions/{id}/participants`.
 */

/** Zgloszenia jednej rozgrywki — wejscie agregatu. */
export interface CompetitionEntries {
  competition: ApiCompetition;
  participants: readonly ApiParticipant[];
}

/** Wiersz tabeli zawodnikow klubu. */
export interface ClubPlayerRow {
  userId: string;
  /** Imie i nazwisko z profilu; „…” dopoki batch profili nie dojdzie. */
  name: string;
  email: string | null;
  /** W ilu rozgrywkach klubu ta osoba ma zgloszenie. */
  competitionCount: number;
  /** Nazwa rozgrywki z najswiezszym zgloszeniem. */
  lastCompetitionName: string;
  lastCompetitionId: string;
  /** Znacznik czasu najswiezszego zgloszenia (ISO). */
  lastJoinedAt: string;
  /** Czy osoba gra teraz — aktywne zgloszenie w trwajacej rozgrywce. */
  playing: boolean;
}

/** Osoby w slocie: jedna w singlu, dwie w deblu i mikscie. */
function membersOf(participant: ApiParticipant): string[] {
  return participant.partnerId ? [participant.userId, participant.partnerId] : [participant.userId];
}

/** Zbiera identyfikatory wszystkich osob ze wszystkich rozgrywek — do batcha profili. */
export function collectClubPlayerIds(entries: readonly CompetitionEntries[]): string[] {
  const ids = new Set<string>();
  for (const entry of entries) {
    for (const participant of entry.participants) {
      for (const userId of membersOf(participant)) ids.add(userId);
    }
  }
  return [...ids];
}

/**
 * Skleja zgloszenia w liste unikalnych zawodnikow.
 *
 * Wycofanych nie pomijamy — byli zawodnikami klubu, a ich zgloszenie to fakt
 * z historii. Znacznik `playing` odrozniania tych, ktorzy graja teraz: wymaga
 * i aktywnego zgloszenia, i trwajacej rozgrywki, bo status `ACTIVE` zostaje
 * na slocie takze po zakonczeniu turnieju przez zwyciezce.
 *
 * Porzadek: alfabetycznie po nazwie, bo lista sluzy do szukania osoby.
 */
export function buildClubPlayerRows(
  entries: readonly CompetitionEntries[],
  profiles: ReadonlyMap<string, UserProfile>,
): ClubPlayerRow[] {
  const rows = new Map<string, ClubPlayerRow>();

  for (const entry of entries) {
    const live = entry.competition.status === 'IN_PROGRESS';

    for (const participant of entry.participants) {
      for (const userId of membersOf(participant)) {
        const profile = profiles.get(userId);
        const existing = rows.get(userId);
        const playing = live && participant.status === 'ACTIVE';

        if (!existing) {
          rows.set(userId, {
            userId,
            name: profile ? `${profile.firstName} ${profile.lastName}`.trim() : '…',
            email: profile?.email ?? null,
            competitionCount: 1,
            lastCompetitionName: entry.competition.name,
            lastCompetitionId: entry.competition.id,
            lastJoinedAt: participant.joinedAt,
            playing,
          });
          continue;
        }

        existing.competitionCount += 1;
        existing.playing = existing.playing || playing;
        // „Ostatnia rozgrywka” to ta z najswiezszym zgloszeniem, a nie ostatnia
        // napotkana — kolejnosc rozgrywek w odpowiedzi nie jest gwarantowana.
        if (participant.joinedAt > existing.lastJoinedAt) {
          existing.lastJoinedAt = participant.joinedAt;
          existing.lastCompetitionName = entry.competition.name;
          existing.lastCompetitionId = entry.competition.id;
        }
      }
    }
  }

  return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name, 'pl-PL'));
}

/** Filtruje wiersze po nazwisku i adresie e-mail. */
export function filterClubPlayerRows(
  rows: readonly ClubPlayerRow[],
  search: string,
): ClubPlayerRow[] {
  const needle = search.trim().toLocaleLowerCase('pl-PL');
  if (!needle) return [...rows];

  return rows.filter((row) => {
    const haystack = `${row.name} ${row.email ?? ''}`.toLocaleLowerCase('pl-PL');
    return haystack.includes(needle);
  });
}
